import { dayjs } from "../datetime/index.js";
const at = (moment) => moment === undefined || moment === null ? dayjs() : dayjs(moment);
const bound = (value) => {
    if (value === undefined || value === null || value === "")
        return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};
const versionNumber = (catalog) => Number(catalog?.version) || 0;
/** Whether the version has started at `moment` (default: now). */
export function hasCatalogStarted(catalog, moment) {
    const from = bound(catalog?.valid_from);
    return Boolean(from) && !from.isAfter(at(moment));
}
/** Whether the version applies at `moment` (default: now): started and not yet expired. */
export function isCatalogActive(catalog, moment) {
    const expires = bound(catalog?.expires);
    return hasCatalogStarted(catalog, moment) && Boolean(expires) && expires.isAfter(at(moment));
}
/**
 * Orders versions by when they start, then by internal version number: negative when `a` is
 * older than `b`. Sorting with it puts the oldest version first.
 */
export function compareCatalogsByStart(a, b) {
    const fromA = bound(a?.valid_from)?.valueOf() ?? Number.NEGATIVE_INFINITY;
    const fromB = bound(b?.valid_from)?.valueOf() ?? Number.NEGATIVE_INFINITY;
    if (fromA !== fromB)
        return fromA < fromB ? -1 : 1;
    return versionNumber(a) - versionNumber(b);
}
/**
 * Index of the version that applies at `moment` (default: now).
 *
 * - Among versions that apply, the one that started last wins, so overlapping dates (a
 *   predecessor whose `expires` was not shortened) still resolve to the newer Fortschreibung.
 * - When none applies (a gap between two versions, or a list loaded before the next version
 *   started), the version that started last is used, so callers keep working on the newest
 *   version they know instead of on nothing.
 * - Versions that have not started are never picked. -1 only when none has started.
 */
export function findActiveCatalogIndex(catalogs, moment) {
    let best = -1;
    let bestActive = false;
    (catalogs ?? []).forEach((catalog, index) => {
        if (!hasCatalogStarted(catalog, moment))
            return;
        const active = isCatalogActive(catalog, moment);
        const better = best === -1 ||
            (active && !bestActive) ||
            (active === bestActive && compareCatalogsByStart(catalog, catalogs[best]) > 0);
        if (better) {
            best = index;
            bestActive = active;
        }
    });
    return best;
}
/** The version that applies at `moment` (default: now); see `findActiveCatalogIndex`. */
export function findActiveCatalog(catalogs, moment) {
    const index = findActiveCatalogIndex(catalogs, moment);
    return index === -1 ? undefined : catalogs[index];
}
// ---------------------------------------------------------------------------
// Validity as calendar days
// ---------------------------------------------------------------------------
const CATALOG_DAY = /^\d{4}-\d{2}-\d{2}$/;
/** Whether `value` is a "YYYY-MM-DD" calendar day that exists. */
export function isCatalogDay(value) {
    return (typeof value === "string" &&
        CATALOG_DAY.test(value) &&
        dayjs.utc(value, "YYYY-MM-DD", true).isValid());
}
/** The calendar day ("YYYY-MM-DD") of a stored validity bound, or null. */
export function toCatalogDay(value) {
    const parsed = bound(value);
    return parsed ? parsed.utc().format("YYYY-MM-DD") : null;
}
/** A stored validity bound for display, "DD.MM.YYYY"; "" when empty. */
export function formatCatalogDay(value) {
    const parsed = bound(value);
    return parsed ? parsed.utc().format("DD.MM.YYYY") : "";
}
/** The stored `valid_from` of a version that starts on `day` ("YYYY-MM-DD"). */
export function catalogStartOfDay(day) {
    return dayjs.utc(day, "YYYY-MM-DD", true).startOf("day").toDate();
}
/** The stored `expires` of a version whose last day is `day` ("YYYY-MM-DD"). */
export function catalogEndOfDay(day) {
    return dayjs.utc(day, "YYYY-MM-DD", true).endOf("day").toDate();
}
/** The calendar day before `day` ("YYYY-MM-DD"), e.g. a predecessor's last day. */
export function previousCatalogDay(day) {
    return dayjs.utc(day, "YYYY-MM-DD", true).subtract(1, "day").format("YYYY-MM-DD");
}
